/**
 * Budget allocation utilities: money formatting, distribution algorithms,
 * and redistribution helpers. All amounts in integer cents.
 *
 * Shared spec (must match backend allocation_calc.go):
 *   - Even: base = total/n, remainder from earliest period forward (one cent per bucket).
 *   - Front-loaded: weights [n, n-1, ..., 1], floor by weight, remainder earliest-first.
 *   - Back-loaded: weights [1, 2, ..., n], floor by weight, remainder latest-first.
 *
 * Test vectors (total_cents, n -> expected slice):
 *   - (1000, 3) even -> [334, 333, 333]
 *   - (1000, 3) front_loaded -> [501, 333, 166]
 *   - (1000, 3) back_loaded -> [166, 333, 501]
 *   - (100, 4) even -> [25, 25, 25, 25]
 *   - (7, 3) even -> [3, 2, 2]
 */

export const ALLOCATION_STRATEGY_OPTIONS = [
  { value: "even", label: "Evenly distributed" },
  { value: "front_loaded", label: "Front-loaded" },
  { value: "back_loaded", label: "Back-loaded" },
];

export const ALLOCATION_STRATEGY_LABELS = {
  even: "Evenly distributed",
  front_loaded: "Front-loaded",
  back_loaded: "Back-loaded",
};

/**
 * Format cents as currency string.
 * @param {number} cents
 * @param {string} [currency='EUR']
 * @returns {string}
 */
export function formatMoney(cents, currency = "EUR") {
  if (cents == null || !Number.isFinite(cents)) return "0";
  const value = Number(cents) / 100;
  // Validate currency to avoid RangeError from toLocaleString on bad codes
  let safeCurrency = typeof currency === "string" ? currency.toUpperCase() : "EUR";
  if (!/^[A-Z]{3}$/.test(safeCurrency)) {
    safeCurrency = "EUR";
  }
  try {
    return value.toLocaleString("nl-NL", {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch (err) {
    // Fallback: best-effort plain numeric string to avoid render-time crashes
    return value.toFixed(2);
  }
}

/**
 * Parse user input to integer cents. Clamps to >= 0.
 * Handles both raw numbers ("1000.50") and formatted currency strings
 * ("€ 1.000,50" nl-NL or "$1,000.50" en-US).
 * @param {string} inputString
 * @returns {number}
 */
export function parseMoneyToCents(inputString) {
  if (inputString == null || typeof inputString !== "string") return 0;
  // Strip everything except digits, commas, periods, and minus sign
  let cleaned = String(inputString).replace(/[^\d,.-]/g, "").trim();
  if (cleaned === "") return 0;

  // Detect locale format by looking at separators.
  // Keep existing heuristic when both separators are present; refine behavior
  // for cases with a single separator like "1,000" vs "1.000" vs "1000,50".
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    // Both separators present:
    //   nl-NL "1.000,50" → last comma after last dot  → comma is decimal
    //   en-US "1,000.50" → last dot after last comma → dot is decimal
    if (lastComma > lastDot) {
      // nl-NL style: dots are thousands separators, comma is decimal
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // en-US style: commas are thousands separators, dot is decimal
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Only commas present. Decide if a lone comma is thousands or decimal.
    const firstComma = cleaned.indexOf(",");
    const onlyOneComma = firstComma === lastComma;
    if (onlyOneComma) {
      const after = cleaned.slice(firstComma + 1);
      const digitsAfter = after.replace(/\D/g, "");
      const isThousands = digitsAfter.length === 3;
      if (isThousands) {
        // Treat as thousands separator and remove it.
        cleaned = cleaned.replace(/,/g, "");
      } else {
        // Treat as decimal separator.
        cleaned = cleaned.replace(",", ".");
      }
    } else {
      // Multiple commas: treat as thousands separators.
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (!hasComma && hasDot) {
    // Only dots present. Decide if dot(s) are thousands or decimal.
    const firstDot = cleaned.indexOf(".");
    const onlyOneDot = firstDot === lastDot;
    if (onlyOneDot) {
      const after = cleaned.slice(firstDot + 1);
      const digitsAfter = after.replace(/\D/g, "");
      const isThousands = digitsAfter.length === 3;
      if (isThousands) {
        // Treat as thousands separator and remove it.
        cleaned = cleaned.replace(/\./g, "");
      }
      // Otherwise, treat as decimal separator and keep the dot as-is.
    } else {
      // Multiple dots (e.g. "1.000.000"): treat dots as thousands separators.
      cleaned = cleaned.replace(/\./g, "");
    }
  }

  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed)) return 0;
  const cents = Math.round(parsed * 100);
  return Math.max(0, cents);
}

/**
 * Sum amountCents across periods.
 * @param {Array<{ amountCents?: number }>} periods
 * @returns {number}
 */
export function sumAllocated(periods) {
  if (!Array.isArray(periods)) return 0;
  return periods.reduce((sum, p) => sum + (Number(p.amountCents) || 0), 0);
}

/**
 * Clamp n to [min, max].
 * @param {number} n
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clampToRange(n, min, max) {
  const num = Number(n);
  if (!Number.isFinite(num)) return min;
  if (num < min) return min;
  if (num > max) return max;
  return num;
}

/**
 * Distribute totalCents evenly across n buckets (integer cents, sum exact).
 * Remainder distributed one cent at a time from the start.
 * @param {number} totalCents
 * @param {number} n
 * @returns {number[]}
 */
export function distributeEven(totalCents, n) {
  const total = Math.max(0, Math.floor(Number(totalCents)));
  const count = Math.max(0, Math.floor(Number(n)));
  if (count === 0) return [];
  const base = Math.floor(total / count);
  const remainder = total - base * count;
  const result = new Array(count).fill(base);
  for (let i = 0; i < remainder && i < count; i++) {
    result[i] += 1;
  }
  return result;
}

/**
 * Front-loaded: first periods get more. Weights [n, n-1, ..., 1].
 * Floor by weight, then add remainder from start.
 * @param {number} totalCents
 * @param {number} n
 * @returns {number[]}
 */
export function distributeFrontLoaded(totalCents, n) {
  const total = Math.max(0, Math.floor(Number(totalCents)));
  const count = Math.max(0, Math.floor(Number(n)));
  if (count === 0) return [];
  const weights = [];
  for (let i = count; i >= 1; i--) weights.push(i);
  const sumWeights = (count * (count + 1)) / 2;
  const raw = weights.map((w) => Math.floor((total * w) / sumWeights));
  let allocated = raw.reduce((s, v) => s + v, 0);
  const remainder = total - allocated;
  const result = [...raw];
  for (let i = 0; i < remainder && i < count; i++) {
    result[i] += 1;
    allocated += 1;
  }
  return result;
}

/**
 * Back-loaded: later periods get more. Weights [1, 2, ..., n].
 * Remainder added from end.
 * @param {number} totalCents
 * @param {number} n
 * @returns {number[]}
 */
export function distributeBackLoaded(totalCents, n) {
  const total = Math.max(0, Math.floor(Number(totalCents)));
  const count = Math.max(0, Math.floor(Number(n)));
  if (count === 0) return [];
  const weights = [];
  for (let i = 1; i <= count; i++) weights.push(i);
  const sumWeights = (count * (count + 1)) / 2;
  const raw = weights.map((w) => Math.floor((total * w) / sumWeights));
  let allocated = raw.reduce((s, v) => s + v, 0);
  const remainder = total - allocated;
  const result = [...raw];
  for (let i = count - 1; i >= 0 && remainder > 0; i--) {
    result[i] += 1;
    allocated += 1;
    if (allocated >= total) break;
  }
  return result;
}

/**
 * Redistribute: keep locked periods unchanged, distribute (total - sumLocked) evenly across unlocked.
 * lockedIds: Set or array of period id.
 * @param {number} totalCents
 * @param {Array<{ id: string, amountCents?: number }>} periods
 * @param {Set<string>|string[]} lockedIds
 * @returns {Array<{ id: string, amountCents: number, [key: string]: unknown }>}
 */
export function redistributeRemainingEven(totalCents, periods, lockedIds) {
  const total = Math.max(0, Math.floor(Number(totalCents)));
  const periodsArr = Array.isArray(periods) ? periods : [];
  const lockedSet = lockedIds instanceof Set ? lockedIds : new Set(lockedIds || []);
  const locked = periodsArr.filter((p) => lockedSet.has(p.id));
  const unlocked = periodsArr.filter((p) => !lockedSet.has(p.id));
  const sumLocked = locked.reduce((s, p) => s + (Number(p.amountCents) || 0), 0);
  const remaining = total - sumLocked;
  if (unlocked.length === 0) {
    return periodsArr.map((p) => ({ ...p, amountCents: Number(p.amountCents) || 0 }));
  }
  const distributed = distributeEven(Math.max(0, remaining), unlocked.length);
  const unlockedWithNewAmounts = unlocked.map((p, i) => ({
    ...p,
    amountCents: distributed[i] ?? 0,
  }));
  const lockedWithSame = locked.map((p) => ({
    ...p,
    amountCents: Number(p.amountCents) || 0,
  }));
  const byId = new Map();
  [...unlockedWithNewAmounts, ...lockedWithSame].forEach((p) => byId.set(p.id, p));
  return periodsArr.map((p) => byId.get(p.id) || { ...p, amountCents: Number(p.amountCents) || 0 });
}

/**
 * Normalize period amounts so they sum to totalCents. Scale proportionally, then fix rounding.
 * If keepZeroEdited, periods with amountCents === 0 that are in editedIds stay 0; rest scaled.
 * @param {number} totalCents
 * @param {Array<{ id: string, amountCents?: number }>} periods
 * @param {boolean} [keepZeroEdited=true]
 * @param {Set<string>|string[]} [editedIds]
 * @returns {Array<{ id: string, amountCents: number, [key: string]: unknown }>}
 */
export function normalizeToTotal(totalCents, periods, keepZeroEdited = true, editedIds = []) {
  const total = Math.max(0, Math.floor(Number(totalCents)));
  const periodsArr = Array.isArray(periods) ? periods : [];
  const editedSet = editedIds instanceof Set ? editedIds : new Set(editedIds || []);
  const zeroEdited = keepZeroEdited
    ? periodsArr.filter((p) => editedSet.has(p.id) && (Number(p.amountCents) || 0) === 0)
    : [];
  const rest = periodsArr.filter((p) => !zeroEdited.some((z) => z.id === p.id));
  const sumRest = rest.reduce((s, p) => s + (Number(p.amountCents) || 0), 0);
  const targetRest = total;
  if (rest.length === 0) {
    return periodsArr.map((p) => ({ ...p, amountCents: Number(p.amountCents) || 0 }));
  }
  if (sumRest <= 0) {
    const even = distributeEven(total, rest.length);
    return periodsArr.map((p) => {
      const idx = rest.findIndex((r) => r.id === p.id);
      if (idx >= 0) return { ...p, amountCents: even[idx] ?? 0 };
      return { ...p, amountCents: Number(p.amountCents) || 0 };
    });
  }
  const scale = targetRest / sumRest;
  const scaled = rest.map((p) => ({
    ...p,
    amountCents: Math.floor((Number(p.amountCents) || 0) * scale),
  }));
  let allocated = scaled.reduce((s, p) => s + p.amountCents, 0);
  const remainder = targetRest - allocated;
  for (let i = 0; i < remainder && i < scaled.length; i++) {
    scaled[i].amountCents += 1;
    allocated += 1;
  }
  const byId = new Map();
  scaled.forEach((p) => byId.set(p.id, p));
  zeroEdited.forEach((p) => byId.set(p.id, { ...p, amountCents: 0 }));
  return periodsArr.map((p) => byId.get(p.id) || { ...p, amountCents: Number(p.amountCents) || 0 });
}
